// App internationalization (i18n). English is the source language; Spanish is
// the first added locale. The chosen locale is persisted to localStorage so it
// survives reloads, and mirrored onto <html lang> for accessibility/SEO.
//
// Vuetify's own component text (data-table footers, date pickers, etc.) is wired
// to the SAME vue-i18n instance via the adapter in plugins/vuetify.js, so those
// switch language too. That is why each locale nests Vuetify's bundled messages
// under the reserved `$vuetify` key.
//
// Messages are plain JSON, so no message-compiler / build plugin is needed:
// createI18n consumes the objects directly (runtime key lookup only).
import { createI18n, useI18n } from 'vue-i18n'
import { en as vuetifyEn, es as vuetifyEs } from 'vuetify/locale'

import en from '../locales/en.json'
import es from '../locales/es.json'

const STORAGE_KEY = 'bv:locale'
export const SUPPORTED_LOCALES = ['en', 'es']
export const DEFAULT_LOCALE = 'en'

// initialLocale prefers a previously-saved choice, then the browser's language,
// then English. localStorage access is guarded because it throws in some private
// / embedded contexts.
function initialLocale() {
  try {
    const saved = localStorage.getItem(STORAGE_KEY)
    if (saved && SUPPORTED_LOCALES.includes(saved)) return saved
  } catch (e) {
    /* localStorage unavailable. Fall through */
  }
  if (typeof navigator !== 'undefined' && navigator.language) {
    const base = navigator.language.slice(0, 2).toLowerCase()
    if (SUPPORTED_LOCALES.includes(base)) return base
  }
  return DEFAULT_LOCALE
}

const i18n = createI18n({
  // Composition mode. globalInjection keeps `$t` / `$i18n` available in every
  // template, including the Options-API components this app is built from.
  legacy: false,
  globalInjection: true,
  locale: initialLocale(),
  fallbackLocale: DEFAULT_LOCALE,
  // Missing keys fall back silently to English rather than warning in the
  // console; a half-translated screen shows English, never a raw key.
  missingWarn: false,
  fallbackWarn: false,
  messages: {
    en: { $vuetify: vuetifyEn, ...en },
    es: { $vuetify: vuetifyEs, ...es },
  },
})

// setLocale switches the active language, persists it, and syncs <html lang>.
// Exported for the language toggle; call this rather than mutating the locale ref
// directly so persistence and the lang attribute stay in step.
export function setLocale(locale) {
  if (!SUPPORTED_LOCALES.includes(locale)) return
  i18n.global.locale.value = locale
  try {
    localStorage.setItem(STORAGE_KEY, locale)
  } catch (e) {
    /* ignore persistence failure */
  }
  syncHtmlLang(locale)
}

export function currentLocale() {
  return i18n.global.locale.value
}

// reportLanguage maps the active UI locale to the backend's ReportLanguage enum,
// so a generated PDF (LOTO sheet, infrared report) comes back in whatever
// language the site is currently in. Only the report's fixed template text is
// translated server-side; authored content is left as written.
export function reportLanguage() {
  return currentLocale() === 'es' ? 'ES' : 'EN'
}

function syncHtmlLang(locale) {
  if (typeof document !== 'undefined' && document.documentElement) {
    document.documentElement.setAttribute('lang', locale)
  }
}

syncHtmlLang(i18n.global.locale.value)

export { useI18n }
export default i18n
